import { FC } from "react";
import { Link } from "react-router-dom";
import { ProjectDataType } from "data/types";
import ProjectCard from "./ProjectCard";
import {
  BuildingOffice2Icon,
  GlobeAltIcon,
  ShieldCheckIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";

export interface SectionProjectsGridProps {
  projects: ProjectDataType[];
  isLoading?: boolean;
  className?: string;
}

const trustItems = [
  { icon: TrophyIcon, label: "50+ Years of Excellence" },
  { icon: BuildingOffice2Icon, label: "Landmark GCC Projects" },
  { icon: GlobeAltIcon, label: "UAE & Middle East Coverage" },
  { icon: ShieldCheckIcon, label: "UK Profiles, Local Delivery" },
];

const SectionProjectsGrid: FC<SectionProjectsGridProps> = ({
  projects,
  isLoading = false,
  className = "",
}) => {
  return (
    <div className={`nc-SectionProjectsGrid ${className}`}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10">
        {trustItems.map((item) => (
          <div
            key={item.label}
            className="flex items-center gap-3 px-4 py-3 rounded-xl border border-neutral-200/80 dark:border-neutral-700 bg-white dark:bg-neutral-900"
          >
            <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center">
              <item.icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </span>
            <span className="text-xs sm:text-sm font-medium text-neutral-700 dark:text-neutral-300 leading-snug">
              {item.label}
            </span>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8 pb-5 border-b border-neutral-200 dark:border-neutral-700">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">
            Project Portfolio
          </h2>
          {!isLoading && (
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              {projects.length > 0
                ? `${projects.length} landmark projects across the UAE and GCC`
                : "No projects to display"}
            </p>
          )}
        </div>
        <Link
          to="/contact"
          className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold rounded-full bcr-btn-primary !py-2.5 shrink-0"
        >
          Discuss Your Project
        </Link>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="animate-pulse rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden bg-white dark:bg-neutral-900"
            >
              <div className="aspect-[4/3] max-h-[160px] bg-neutral-200 dark:bg-neutral-700" />
              <div className="p-3 space-y-2">
                <div className="h-3 w-3/4 rounded bg-neutral-200 dark:bg-neutral-700" />
                <div className="h-2 w-1/2 rounded bg-neutral-200 dark:bg-neutral-700" />
              </div>
            </div>
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-16 sm:py-20 px-6 rounded-2xl border border-dashed border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-800/40">
          <BuildingOffice2Icon className="w-12 h-12 mx-auto text-neutral-400" />
          <h3 className="mt-4 text-lg font-semibold text-neutral-900 dark:text-white">
            No projects available
          </h3>
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
            Check back soon or contact us about your upcoming development.
          </p>
          <Link to="/contact" className="mt-6 inline-block bcr-btn-primary text-sm">
            Get in touch
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      )}
    </div>
  );
};

export default SectionProjectsGrid;
